import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  useTheme,
  Avatar,
  Stack
} from '@mui/material';

// Favicon component
const Favicon = ({ company, size = 24 }) => {
  const analysisResults = useSelector((state) => state.competitorsReducer.competitorsAnalysisResults.analysis_results);

  // Find the matching company's favicon from analysis_results
  const favicon = analysisResults?.find((item) => {
    const companyDomain = company.toLowerCase().replace(/^https?:\/\//, '');
    const itemDomain = item.url.toLowerCase().replace(/^https?:\/\//, '');
    return itemDomain.includes(companyDomain) || companyDomain.includes(itemDomain);
  })?.favicon;

  return (
    <Avatar src={favicon} sx={{ width: size, height: size }}>
      {company.charAt(0)}
    </Avatar>
  );
};

const HeatmapScorecard = () => {
  const theme = useTheme();

  // Get data from Redux
  const sbsData = useSelector((state) => state.competitorsReducer.competitorsAnalysisResults?.side_by_side_analysis);

  // Access the analysis object
  const analysis = sbsData?.analysis || {};

  // Get heatmap scorecard data
  const scoreCardData = analysis['7_multi_dimensional_comparison_matrices']?.A_heatmap_scorecard || [];

  // Get parameters for the table (excluding company and total)
  const parameters = scoreCardData.length > 0 ? Object.keys(scoreCardData[0]).filter((key) => key !== 'company' && key !== 'total') : [];

  // Get parameter definitions from the scoring_parameters section
  const parameterDefinitions = (analysis['3_scoring_parameters'] || []).reduce((acc, param) => {
    acc[param.id] = {
      1: param['1'],
      3: param['3'],
      5: param['5']
    };
    return acc;
  }, {});

  // Get parameter weights
  const parameterWeights = (analysis['4_weight_assignment'] || []).reduce((acc, param) => {
    acc[param.id] = param.weight_percent;
    return acc;
  }, {});

  // State to track which companies are displayed
  const [displayedCompanies, setDisplayedCompanies] = useState(
    scoreCardData.map((item) => item.company).reduce((acc, company) => ({ ...acc, [company]: true }), {})
  );

  // Toggle company display
  const handleCompanyToggle = (company) => {
    setDisplayedCompanies({
      ...displayedCompanies,
      [company]: !displayedCompanies[company]
    });
  };

  // Format parameter names for display
  const formatParameterName = (param) => {
    return param
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get color based on score
  const getScoreColor = (score) => {
    if (score >= 4.5) return theme.palette.success.dark;
    if (score >= 3.5) return theme.palette.success.main;
    if (score >= 2.5) return theme.palette.warning.main;
    if (score >= 1.5) return theme.palette.warning.dark;
    return theme.palette.error.main;
  };

  // Get tooltip text for score explanation
  const getScoreTooltip = (parameter, score) => {
    if (score >= 4.5) return parameterDefinitions[parameter]['5'];
    if (score >= 3.5) return `Between: ${parameterDefinitions[parameter]['3']} and ${parameterDefinitions[parameter]['5']}`;
    if (score >= 2.5) return parameterDefinitions[parameter]['3'];
    if (score >= 1.5) return `Between: ${parameterDefinitions[parameter]['1']} and ${parameterDefinitions[parameter]['3']}`;
    return parameterDefinitions[parameter]['1'];
  };

  // Sort companies by total score
  const sortedCompanies = [...scoreCardData].sort((a, b) => b.total - a.total);

  // Filter displayed companies
  const filteredCompanies = sortedCompanies.filter((company) => displayedCompanies[company.company]);

  return (
    <Box sx={{ mb: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h4" gutterBottom>
            Competitive Heatmap Scorecard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Color-coded performance comparison across key parameters
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {/* Company Selection Checkboxes */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 3 }}>
            <FormGroup row>
              {sortedCompanies.map((company) => (
                <FormControlLabel
                  key={company.company}
                  control={
                    <Checkbox
                      checked={displayedCompanies[company.company]}
                      onChange={() => handleCompanyToggle(company.company)}
                      name={company.company}
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Favicon company={company.company} size={20} />
                      <Typography variant="body2">{company.company}</Typography>
                    </Stack>
                  }
                />
              ))}
            </FormGroup>
          </Box>

          {/* Scorecard Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Company</TableCell>
                  {parameters.map((param) => (
                    <Tooltip key={param} title={`Weight: ${parameterWeights[param]}%`} placement="top">
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          textAlign: 'center',
                          minWidth: '80px'
                        }}
                      >
                        {formatParameterName(param)}
                      </TableCell>
                    </Tooltip>
                  ))}
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      textAlign: 'center',
                      bgcolor: theme.palette.grey[200]
                    }}
                  >
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCompanies.map((company) => (
                  <TableRow key={company.company}>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Favicon company={company.company} size={24} />
                        <Typography>{company.company}</Typography>
                      </Stack>
                    </TableCell>
                    {parameters.map((param) => (
                      <Tooltip key={`${company.company}-${param}`} title={getScoreTooltip(param, company[param])} placement="top">
                        <TableCell
                          sx={{
                            bgcolor: getScoreColor(company[param]),
                            color: 'white',
                            textAlign: 'center',
                            fontWeight: 'bold'
                          }}
                        >
                          {company[param]}
                        </TableCell>
                      </Tooltip>
                    ))}
                    <TableCell
                      sx={{
                        bgcolor: theme.palette.grey[200],
                        textAlign: 'center',
                        fontWeight: 'bold'
                      }}
                    >
                      {company.total}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Legend */}
          <Box sx={{ mt: 3, display: 'flex', gap: 3 }}>
            <Typography variant="subtitle2">Score Legend:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 20, height: 20, bgcolor: theme.palette.success.dark, mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                4.5-5: Excellent
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 20, height: 20, bgcolor: theme.palette.success.main, mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                3.5-4.4: Good
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 20, height: 20, bgcolor: theme.palette.warning.main, mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                2.5-3.4: Average
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 20, height: 20, bgcolor: theme.palette.warning.dark, mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                1.5-2.4: Below Average
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 20, height: 20, bgcolor: theme.palette.error.main, mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                1-1.4: Poor
              </Typography>
            </Box>
          </Box>

          {/* Instructions */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Hover over parameter names to see their weightage in the overall score. Hover over individual scores to see the criteria
            description for that score level.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default HeatmapScorecard;
